import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Sparkles, Share as ShareIcon, PlusSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isStandalone } from "@/lib/pwa";

// -----------------------------------------------------------------------------
// Persistence keys
// -----------------------------------------------------------------------------
const DISMISS_KEY = "vu-pwa-install-dismissed-at";
const DISMISS_KIND_KEY = "vu-pwa-install-dismiss-kind";     // "later" | "close"
const CELEBRATED_KEY = "vu-pwa-installer-celebrated";
const IOS_HIDE_KEY = "vu-pwa-ios-hide-forever";              // "1" if user tapped "Don't show again"
const IOS_NONSAFARI_HIDE_KEY = "vu-pwa-ios-non-safari-hide";  // "1" if Chrome/Firefox iOS user dismissed the "Open in Safari" hint
const INTERACTION_COUNT_KEY = "vu-pwa-interaction-count";    // # of authed page views

// -----------------------------------------------------------------------------
// Cooldowns
// -----------------------------------------------------------------------------
const DAYS = { later: 7, close: 30 };
const DELAY_MS = 30_000;              // show earliest 30s after mount on eligible pages
const INTERACTION_TRIGGER = 3;        // OR after this many authed page views
const IOS_HINT_DELAY_MS = 20_000;     // iOS hint even lazier — Safari has no native prompt

// -----------------------------------------------------------------------------
// Routes where the prompt is suppressed. The user is in flow — do not interrupt.
// -----------------------------------------------------------------------------
const SUPPRESS_PATHS = new Set([
    "/login",
    "/signup",
    "/forgot",
    "/reset-password",
    "/verify-email",
    "/auth/callback",
    "/choose-role",
    "/billing",
    "/billing/success",
]);

function isSuppressedPath(pathname) {
    if (!pathname) return false;
    if (SUPPRESS_PATHS.has(pathname)) return true;
    // Anything under /billing/ or /auth/ is off-limits too
    return pathname.startsWith("/billing/") || pathname.startsWith("/auth/");
}

// -----------------------------------------------------------------------------
// UA sniffing (last resort — only for iOS Safari where beforeinstallprompt never fires)
// -----------------------------------------------------------------------------
function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isIOSSafari() {
    if (!isIOS()) return false;
    const ua = navigator.userAgent || "";
    // Exclude in-app browsers (FB/Instagram/Chrome iOS/Firefox iOS) which can't A2HS reliably.
    const isChromeiOS = /CriOS/i.test(ua);
    const isFirefoxiOS = /FxiOS/i.test(ua);
    const isFBiOS = /FBAN|FBAV|Instagram/i.test(ua);
    return !isChromeiOS && !isFirefoxiOS && !isFBiOS;
}
/**
 * True for iOS browsers that CANNOT install PWAs due to Apple's WebKit lockdown:
 * Chrome iOS, Firefox iOS, Edge iOS, in-app browsers (FB/Instagram).
 * Only Safari has permission to A2HS on iOS.
 */
function isIOSNonSafari() {
    if (!isIOS()) return false;
    const ua = navigator.userAgent || "";
    return /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|DuckDuckGo|FBAN|FBAV|Instagram/i.test(ua);
}

// -----------------------------------------------------------------------------
// Local-storage helpers (all wrapped so private-mode Safari can't throw)
// -----------------------------------------------------------------------------
function readLS(key) {
    try { return localStorage.getItem(key); } catch { return null; }
}
function writeLS(key, value) {
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function removeLS(key) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
}

function wasRecentlyDismissed() {
    const raw = readLS(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (Number.isNaN(at)) return false;
    const kind = readLS(DISMISS_KIND_KEY) === "close" ? "close" : "later";
    const cooldownMs = DAYS[kind] * 24 * 60 * 60 * 1000;
    return Date.now() - at < cooldownMs;
}

// -----------------------------------------------------------------------------
// Celebrate a successful install: mark the perk server-side and show a toast
// once per browser. Idempotent + silent on repeat sessions.
// -----------------------------------------------------------------------------
async function celebrateInstall({ silent = false } = {}) {
    try {
        const already = readLS(CELEBRATED_KEY) === "1";
        api.post("/billing/mark-pwa-install").catch(() => {});
        if (already || silent) return;
        writeLS(CELEBRATED_KEY, "1");
        toast.success("You're in! Welcome to Virtually Unstoppable.", {
            description:
                "As a thank-you for installing, you've unlocked 10% off your first paid month — applied automatically at checkout.",
            duration: 10000,
            icon: <Sparkles size={18} />,
        });
    } catch { /* non-fatal */ }
}

/**
 * PWA install experience — refined per product spec.
 *
 * States:
 *   • hidden — dismissed, ineligible, standalone, or suppressed route
 *   • android — Chrome/Edge/Android with beforeinstallprompt captured
 *   • ios    — iOS Safari (no native prompt; show 3-step instructions)
 *
 * Timing gate: never shows on onboarding/checkout routes; delays by 45s OR
 *              3 route changes once the app becomes installable.
 *
 * Dismissal:
 *   • "Maybe Later" → hide for 7 days
 *   • "✕"           → hide for 30 days
 *   • iOS "Don't show again" → hide forever (until localStorage cleared)
 *   • Successful install → hide immediately + celebration toast + perk
 */
export default function PWAInstallPrompt() {
    const location = useLocation();
    // "hidden" | "android" | "ios" | "ios-safari-only"
    const [ui, setUi] = useState("hidden");
    const [installable, setInstallable] = useState(
        typeof window !== "undefined" && !!window.__vuDeferredInstall
    );
    const [gateOpen, setGateOpen] = useState(false);
    const [forceShow, setForceShow] = useState(false);

    // -------------------------------------------------------------------------
    // Interaction counter — persisted so hard reloads still count. Once the
    // authenticated user has visited a non-suppressed route N times we open
    // the gate immediately.
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (isSuppressedPath(location.pathname)) return;
        const prev = parseInt(readLS(INTERACTION_COUNT_KEY) || "0", 10) || 0;
        const next = prev + 1;
        writeLS(INTERACTION_COUNT_KEY, String(next));
        if (next >= INTERACTION_TRIGGER) setGateOpen(true);
    }, [location.pathname]);

    // -------------------------------------------------------------------------
    // Passive listeners: beforeinstallprompt + appinstalled + standalone check
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (isStandalone()) {
            // Already installed — celebrate silently on repeat visits, never show banner
            celebrateInstall({ silent: readLS(CELEBRATED_KEY) === "1" });
            setUi("hidden");
            return;
        }

        const onInstallable = () => setInstallable(true);
        const onInstalled = () => {
            removeLS(DISMISS_KEY);
            removeLS(DISMISS_KIND_KEY);
            removeLS(INTERACTION_COUNT_KEY);
            setUi("hidden");
            celebrateInstall();
        };
        window.addEventListener("vu:installable", onInstallable);
        window.addEventListener("vu:installed", onInstalled);

        // Manual trigger from the "Download app" button in the header — this
        // bypasses the interaction/delay gate AND ignores dismissal cooldowns
        // so the user always has an on-demand escape hatch.
        const onForceShow = () => {
            removeLS(DISMISS_KEY);
            removeLS(DISMISS_KIND_KEY);
            removeLS(IOS_HIDE_KEY);
            removeLS(IOS_NONSAFARI_HIDE_KEY);
            setForceShow(true);
            setGateOpen(true);
        };
        window.addEventListener("vu:force-show-install", onForceShow);

        // Delay-based gate — opens after DELAY_MS (Android) or IOS_HINT_DELAY_MS (iOS)
        const delay = isIOSSafari() ? IOS_HINT_DELAY_MS : DELAY_MS;
        const t = setTimeout(() => setGateOpen(true), delay);

        return () => {
            window.removeEventListener("vu:installable", onInstallable);
            window.removeEventListener("vu:installed", onInstalled);
            window.removeEventListener("vu:force-show-install", onForceShow);
            clearTimeout(t);
        };
    }, []);

    // -------------------------------------------------------------------------
    // Decide whether the banner should be visible right now
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (isStandalone()) { setUi("hidden"); return; }
        // Force-show (from the top-bar Download button) bypasses route suppression
        // and dismissal cooldowns — the user asked for it, honour it.
        if (!forceShow) {
            if (isSuppressedPath(location.pathname)) { setUi("hidden"); return; }
            if (wasRecentlyDismissed()) { setUi("hidden"); return; }
            if (!gateOpen) { setUi("hidden"); return; }
        }

        if (installable) {
            setUi("android");
            return;
        }
        // iOS Safari fallback — force-show ignores the "Don't show again" flag
        if (isIOSSafari() && (forceShow || readLS(IOS_HIDE_KEY) !== "1")) {
            setUi("ios");
            return;
        }
        // iOS Chrome / Firefox / Edge / in-app browser — Apple forbids PWA install
        // in every iOS browser except Safari. Guide the user to open in Safari.
        if (isIOSNonSafari() && (forceShow || readLS(IOS_NONSAFARI_HIDE_KEY) !== "1")) {
            setUi("ios-safari-only");
            return;
        }
        // Desktop / Android with no captured prompt yet — force-show still shows
        // the Android card so we can display "How to install manually" guidance.
        if (forceShow) {
            setUi("android");
            return;
        }
        setUi("hidden");
    }, [location.pathname, installable, gateOpen, forceShow]);

    // -------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------
    const install = async () => {
        const evt = window.__vuDeferredInstall;
        if (!evt) return;
        try {
            evt.prompt();
            const choice = await evt.userChoice;
            if (choice?.outcome === "accepted") {
                celebrateInstall();
                removeLS(DISMISS_KEY);
                removeLS(DISMISS_KIND_KEY);
                setUi("hidden");
            }
        } catch { /* ignore */ }
        window.__vuDeferredInstall = null;
        setInstallable(false);
    };

    const maybeLater = () => {
        writeLS(DISMISS_KEY, String(Date.now()));
        writeLS(DISMISS_KIND_KEY, "later");
        setForceShow(false);
        setUi("hidden");
    };

    const close = () => {
        writeLS(DISMISS_KEY, String(Date.now()));
        writeLS(DISMISS_KIND_KEY, "close");
        setForceShow(false);
        setUi("hidden");
    };

    const iosDontShowAgain = () => {
        writeLS(IOS_HIDE_KEY, "1");
        setForceShow(false);
        setUi("hidden");
    };

    const iosNonSafariDismiss = () => {
        writeLS(IOS_NONSAFARI_HIDE_KEY, "1");
        setForceShow(false);
        setUi("hidden");
    };

    const copySafariLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.origin);
            toast.success("Link copied", {
                description: "Open Safari, paste the link and try installing again.",
                duration: 6000,
            });
        } catch {
            toast.info("Copy this URL into Safari:", {
                description: window.location.origin,
                duration: 8000,
            });
        }
    };

    if (ui === "hidden") return null;

    // -------------------------------------------------------------------------
    // Android / Chrome / Edge — spec-exact copy + three buttons
    // -------------------------------------------------------------------------
    if (ui === "android") {
        const canPrompt = !!window.__vuDeferredInstall;
        return (
            <Shell onClose={close}>
                <div className="p-5 pr-12">
                    <div className="flex items-start gap-3">
                        <img
                            src="/icons/icon-96.png"
                            alt=""
                            className="h-11 w-11 rounded-xl shrink-0"
                        />
                        <div className="min-w-0">
                            <div className="font-serif font-semibold text-[17px] leading-tight">
                                <span aria-hidden="true">📱 </span>
                                Install Virtually Unstoppable
                            </div>
                            <p className="text-sm text-[color:var(--text-ink)]/70 mt-1.5 leading-snug">
                                {canPrompt
                                    ? "Access your workspace instantly from your home screen for a faster, app-like experience."
                                    : "Your browser is ready to install this site as an app — one tap from your address bar."}
                            </p>
                        </div>
                    </div>

                    {/* If beforeinstallprompt hasn't fired yet, show a fallback
                        "menu → Install app" hint so the user always has a path. */}
                    {!canPrompt && (
                        <ol className="mt-3 space-y-2 text-sm text-[color:var(--text-ink)]/85">
                            <li className="flex items-start gap-2.5">
                                <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">1</span>
                                <span>Open your browser&rsquo;s <span className="font-medium">⋮ menu</span> (top-right on Android, top-right on desktop).</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">2</span>
                                <span>Tap <span className="font-medium">&ldquo;Install app&rdquo;</span> or <span className="font-medium">&ldquo;Add to Home screen&rdquo;</span>.</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">3</span>
                                <span>Confirm &mdash; you&rsquo;re done.</span>
                            </li>
                        </ol>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        {canPrompt && (
                            <button
                                type="button"
                                onClick={install}
                                data-testid="pwa-install-btn"
                                className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2 min-h-[44px]"
                            >
                                <Download size={16} /> Install
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={maybeLater}
                            data-testid="pwa-install-later"
                            className="btn-ghost text-sm px-4 py-2 min-h-[44px]"
                        >
                            {canPrompt ? "Maybe Later" : "Got it"}
                        </button>
                    </div>
                </div>
            </Shell>
        );
    }

    // -------------------------------------------------------------------------
    // iOS non-Safari (Chrome/Firefox/Edge/in-app) — Apple forbids install here.
    // Explain the WebKit restriction and offer to copy the URL for Safari.
    // -------------------------------------------------------------------------
    if (ui === "ios-safari-only") {
        return (
            <Shell onClose={iosNonSafariDismiss}>
                <div className="p-5 pr-12">
                    <div className="flex items-start gap-3">
                        <img
                            src="/icons/icon-96.png"
                            alt=""
                            className="h-11 w-11 rounded-xl shrink-0"
                        />
                        <div className="min-w-0">
                            <div className="font-serif font-semibold text-[17px] leading-tight">
                                <span aria-hidden="true">📱 </span>
                                Install on your iPhone
                            </div>
                            <p className="text-sm text-[color:var(--text-ink)]/75 mt-1.5 leading-snug">
                                Apple only lets <span className="font-medium">Safari</span> add apps to your home screen on iPhone. Open this page in Safari to install &mdash; it takes 10 seconds.
                            </p>
                        </div>
                    </div>

                    <ol className="mt-3 space-y-2 text-sm text-[color:var(--text-ink)]/85">
                        <li className="flex items-start gap-2.5">
                            <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">1</span>
                            <span>Tap <span className="font-medium">Copy link</span> below.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">2</span>
                            <span>Open <span className="font-medium">Safari</span> and paste the link.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">3</span>
                            <span>Tap <ShareIcon size={13} className="inline-block align-text-bottom text-[var(--brand-purple)]" /> <span className="font-medium">Share</span> &rarr; <span className="font-medium">&ldquo;Add to Home Screen&rdquo;</span>.</span>
                        </li>
                    </ol>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={copySafariLink}
                            data-testid="pwa-copy-for-safari"
                            className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2 min-h-[44px]"
                        >
                            <Download size={16} /> Copy link for Safari
                        </button>
                        <button
                            type="button"
                            onClick={iosNonSafariDismiss}
                            data-testid="pwa-ios-nonsafari-dismiss"
                            className="btn-ghost text-sm px-4 py-2 min-h-[44px]"
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </Shell>
        );
    }

    // -------------------------------------------------------------------------
    // iOS Safari — 3-step instructional card + Don't show again
    // -------------------------------------------------------------------------
    return (
        <Shell onClose={close}>
            <div className="p-5 pr-12">
                <div className="flex items-start gap-3">
                    <img
                        src="/icons/icon-96.png"
                        alt=""
                        className="h-11 w-11 rounded-xl shrink-0"
                    />
                    <div className="min-w-0">
                        <div className="font-serif font-semibold text-[17px] leading-tight">
                            <span aria-hidden="true">📱 </span>
                            Install Virtually Unstoppable
                        </div>
                        <p className="text-sm text-[color:var(--text-ink)]/70 mt-1.5 leading-snug">
                            To install on iPhone or iPad:
                        </p>
                    </div>
                </div>

                <ol className="mt-3 space-y-2 text-sm text-[color:var(--text-ink)]/85">
                    <li className="flex items-start gap-2.5">
                        <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">
                            1
                        </span>
                        <span>
                            Tap the <ShareIcon size={13} className="inline-block align-text-bottom text-[var(--brand-purple)]" />{" "}
                            <span className="font-medium">Share</span> button in Safari.
                        </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                        <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">
                            2
                        </span>
                        <span>
                            Choose <PlusSquare size={13} className="inline-block align-text-bottom text-[var(--brand-purple)]" />{" "}
                            <span className="font-medium">&ldquo;Add to Home Screen&rdquo;</span>.
                        </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                        <span className="mt-[2px] shrink-0 h-5 w-5 rounded-full bg-gradient-to-br from-[var(--brand-purple)] to-[var(--brand-orange)] text-white text-[11px] font-semibold grid place-items-center">
                            3
                        </span>
                        <span>
                            Tap <span className="font-medium">Add</span> — you&rsquo;ll find
                            Virtually Unstoppable next to your favourite apps.
                        </span>
                    </li>
                </ol>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={maybeLater}
                        data-testid="pwa-install-later"
                        className="btn-ghost text-sm px-4 py-2 min-h-[44px]"
                    >
                        Maybe Later
                    </button>
                    <button
                        type="button"
                        onClick={iosDontShowAgain}
                        data-testid="pwa-install-ios-hide"
                        className="text-xs text-[var(--text-ink)]/55 hover:text-[var(--text-ink)] px-2 py-2 min-h-[44px]"
                    >
                        Don&rsquo;t show again
                    </button>
                </div>
            </div>
        </Shell>
    );
}

// -----------------------------------------------------------------------------
// Presentational shell — kept outside the component to satisfy React linting
// (no-unstable-nested-components) and preserve state across re-renders.
// -----------------------------------------------------------------------------
function Shell({ children, onClose }) {
    return (
        <div
            data-testid="pwa-install-prompt"
            role="dialog"
            aria-label="Install Virtually Unstoppable"
            className="fixed left-1/2 -translate-x-1/2 bottom-20 md:bottom-6 z-[9998] w-[calc(100%-1.5rem)] max-w-md animate-in fade-in slide-in-from-bottom-4 duration-300"
            style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(26,19,24,0.08)] bg-[var(--bg-cream)] shadow-[0_18px_48px_-18px_rgba(123,45,142,0.32)]">
                <div
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[var(--brand-purple)] via-[var(--brand-magenta)] to-[var(--brand-orange)]"
                />
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close (don't show for 30 days)"
                    title="Close (don't show for 30 days)"
                    data-testid="pwa-install-close"
                    className="absolute top-2.5 right-2.5 rounded-full p-2 text-[var(--text-ink)]/55 hover:text-[var(--text-ink)] hover:bg-black/5 transition min-h-[36px] min-w-[36px] grid place-items-center"
                >
                    <X size={16} />
                </button>
                {children}
            </div>
        </div>
    );
}
